import { useState, useEffect, useCallback } from "react";
import axiosInstance from "services/httpInterceptor";

/**
 * Fetches paginated press articles for the v2 feed.
 * Topic filtering is done client-side by matching analysis.topic
 * (a free-text phrase like "Flow Diverters") — NOT analysis.type (the taxonomy category).
 * These are different fields; the backend filter only handles analysis.type.
 */
const usePressArticleFeed = (selectedPress, selectedPresetKey, selectedTopic) => {
  // All articles for the current source/period window (unfiltered)
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalElements, setTotalElements] = useState(0);

  // Pagination for the filtered view
  const PAGE_SIZE = 12;
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  const fetchArticles = useCallback(async () => {
    if (!selectedPresetKey) return;

    setLoading(true);
    setError(null);
    setDisplayCount(PAGE_SIZE);

    try {
      // Fetch a large page to get all articles in the window.
      // Press sources typically produce 50–200 articles/quarter — fits in one request.
      const params = {
        preset: selectedPresetKey.toUpperCase(),
        page: 0,
        size: 200,
      };
      if (selectedPress) params.source = selectedPress;

      const response = await axiosInstance.get("/v1/news/presses/paged", { params });
      const data = response.data;
      setAllArticles(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (err) {
      setError(err);
      setAllArticles([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPress, selectedPresetKey]);

  // Refetch when source or period changes
  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Reset display count when topic filter changes
  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
  }, [selectedTopic]);

  // Client-side topic filter — match analysis.topic (free-text phrase)
  const filtered = selectedTopic
    ? allArticles.filter(
        (a) => a.analysis?.topic?.toLowerCase() === selectedTopic.toLowerCase()
      )
    : allArticles;

  const displayed = filtered.slice(0, displayCount);
  const hasMore = displayCount < filtered.length;
  const loadMore = () => setDisplayCount((c) => c + PAGE_SIZE);

  return {
    articles: displayed,
    loading,
    error,
    hasMore,
    loadMore,
    totalElements: selectedTopic ? filtered.length : totalElements,
  };
};

export default usePressArticleFeed;
